import React, { useEffect, useMemo } from 'react';
import { GoogleMap, Marker, StreetViewPanorama } from '@react-google-maps/api';
import {
  darkMapStyle,
  hippoMoods,
  MAP_MARKER,
  WORLD_MAP_CENTER,
  WORLD_MAP_ZOOM,
} from '../../../api';

import { getRandomHippo } from '../../../helpers/arrays';

interface IGoogleMapComponent {
  isStreetView: boolean;
  isDestinationLoaded: boolean;
  coordinates: { lat: number; lng: number } | null;
  // eslint-disable-next-line no-undef
  onMapClick: (event: google.maps.MapMouseEvent) => void;
  latitude: number;
  longitude: number;
}

const containerStyle = {
  width: '100%',
  height: '100vh',
};

const GoogleMapComponent: React.FC<IGoogleMapComponent> = ({
  isStreetView,
  isDestinationLoaded,
  coordinates,
  onMapClick,
  latitude,
  longitude,
}) => {
  const WORLD_MAP_OPTIONS = useMemo(
    () => ({
      styles: darkMapStyle,
      disableDefaultUI: false,
      streetViewControl: false,
      mapTypeControl: false,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google?.maps?.ControlPosition.LEFT_CENTER,
      },
      fullscreenControl: false,
      restriction: {
        latLngBounds: {
          north: 85,
          south: -85,
          west: -180,
          east: 180,
        },
        strictBounds: true,
      },
    }),
    [],
  );

  const options = useMemo(
    () => ({
      position: { lat: latitude, lng: longitude },
      pov: { heading: 100, pitch: 0 },
      zoom: 1,
      visible: true,
      addressControl: false,
      linksControl: false,
      clickToGo: false,
      showRoadLabels: false,
      enableCloseButton: false,
      fullscreenControl: false,
      zoomControl: false,
      zoomControlOptions: {
        position: window.google?.maps?.ControlPosition.LEFT_CENTER,
      },
      rotateControl: false,
      motionTracking: false,
      motionTrackingControl: false,
    }),
    [latitude, longitude],
  );

  const hippoStreetViewImage = getRandomHippo(hippoMoods);
  const hippoStreetViewImageSrc = hippoStreetViewImage.src;
  const hippoStreetViewImageWidth = hippoStreetViewImage.size.width;
  const hippoStreetViewImageHeight = hippoStreetViewImage.size.height;

  useEffect(() => {
    if (typeof DeviceMotionEvent !== 'undefined') {
      try {
        Object.defineProperty(DeviceMotionEvent, 'requestPermission', {
          value: undefined,
        });
      } catch (e) {
        console.warn('Error in disabling gyroscope:', e);
      }
    }
  }, []);

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={WORLD_MAP_CENTER}
      zoom={WORLD_MAP_ZOOM}
      options={WORLD_MAP_OPTIONS}
      onClick={onMapClick}
    >
      {isStreetView && isDestinationLoaded ? (
        <>
          <Marker
            position={{ lat: options.position.lat, lng: options.position.lng }}
            icon={{
              url: hippoStreetViewImageSrc,
              scaledSize: new window.google.maps.Size(
                hippoStreetViewImageWidth,
                hippoStreetViewImageHeight,
              ),
            }}
          />

          <StreetViewPanorama options={options} />
        </>
      ) : (
        coordinates && (
          <Marker
            position={coordinates}
            icon={{
              url: MAP_MARKER,
              scaledSize: new window.google.maps.Size(50, 62),
            }}
          />
        )
      )}
    </GoogleMap>
  );
};

export default React.memo(GoogleMapComponent);
